import { createFileRoute, notFound } from "@tanstack/react-router";
import { ServiceLanding, buildLandingSchemas } from "@/components/ServiceLanding";
import { canonicalAndHreflang, ctaDesc, ctaTitle } from "@/lib/seo";
import { findPanScenario, PAN_SCENARIOS } from "@/data/pan-card-scenarios";

export const Route = createFileRoute("/pan-card/$scenario")({
  loader: ({ params }) => {
    const s = findPanScenario(params.scenario);
    if (!s) throw notFound();
    return { s };
  },
  head: ({ params }) => {
    const s = findPanScenario(params.scenario);
    if (!s) return { meta: [{ title: ctaTitle("Not found", "/pan-card/$scenario", "th")}, { name: "robots", content: "noindex" }] };
    const path = `/pan-card/${s.slug}`;
    const TITLE = `PAN Card สำหรับ ${s.th} (Form ${s.form}) — ยื่นจากไทย | NYC`;
    const DESC = `บริการขอ PAN Card อินเดียสำหรับ ${s.th} (${s.en}) ผ่าน Form ${s.form} เสร็จ ${s.turnaround} — ${s.reason} — ทักไลน์เพื่อรับเช็กลิสต์เอกสารทันที`;
    return {
      meta: [
        { title: ctaTitle(TITLE, "/pan-card/$scenario", "th")}, { name: "description", content: ctaDesc(DESC, "/pan-card/$scenario", "th")},
        { property: "og:title", content: ctaTitle(TITLE, "/pan-card/$scenario", "th")}, { property: "og:description", content: ctaDesc(DESC, "/pan-card/$scenario", "th")},
        { property: "og:url", content: `https://nyclanguageinstitute.com${path}` },
      ],
      links: canonicalAndHreflang(path, `/en${path}`),
      scripts: buildLandingSchemas({
        path, title: TITLE, description: DESC,
        serviceType: `PAN Card — ${s.en}`, priceFromThb: s.priceFrom,
        steps: [
          { name: "ประเมินความเหมาะสม", text: `ตรวจสิทธิ์ ${s.en} — เลือก Form ${s.form}` },
          { name: "เตรียมเอกสาร", text: s.docs.join(" · ") },
          { name: "แปล + Notary + Apostille", text: "เอกสารต่างชาติต้อง Apostille (Hague 15 ธ.ค. 2025) หรือ Embassy Legalization" },
          { name: "ยื่น NSDL/UTIITSL", text: "ยื่นออนไลน์ + ส่งเอกสารด่วนไปมุมไบ" },
          { name: "รับ e-PAN + บัตรจริง", text: `เสร็จภายใน ${s.turnaround}` },
        ],
        faqs: [
          { q: `ทำไม ${s.th} ต้องมี PAN?`, a: s.reason },
          { q: "ใช้ Form อะไร?", a: `Form ${s.form}` },
          { q: "เอกสารที่ต้องเตรียม?", a: s.docs.join(" · ") },
          { q: "ราคาและเวลา?", a: `เริ่ม ฿${s.priceFrom.toLocaleString()} เสร็จ ${s.turnaround}` },
          { q: "e-PAN vs. บัตรจริง?", a: "e-PAN เป็นไฟล์ PDF ที่ Income Tax Department อินเดียลงลายเซ็นดิจิทัลอย่างเป็นทางการ ใช้ได้ครบทุกวัตถุประสงค์ทั้ง KYC ธนาคาร กองทุนรวม จดทะเบียนบริษัท ยื่นภาษี และซื้อขายอสังหาริมทรัพย์ในอินเดีย ส่วนบัตรพลาสติกจริงเป็นเพียงของแถมส่งจาก NSDL Pune มาไทยผ่าน India Post/DHL อีก 10–15 วันทำการ ไม่จำเป็นสำหรับธุรกรรมออนไลน์ใดๆ" },
        ],
        breadcrumbs: [
          { name: "Home", path: "/" },
          { name: "PAN Card", path: "/pan-card" },
          { name: s.th, path },
        ],
      }),
    };
  },
  component: Page,
  notFoundComponent: () => (
    <main className="p-12"><h1 className="text-2xl font-bold">ไม่พบประเภทผู้ยื่น</h1></main>
  ),
});

function Page() {
  const { s } = Route.useLoaderData();
  return (
    <ServiceLanding
      badge={`PAN · Form ${s.form}`}
      title={`PAN Card สำหรับ ${s.th}`}
      subtitle={`${s.en} — ${s.reason}`}
      quickAnswer={{
        q: `${s.th} ต้องมี PAN Card ไหม?`,
        a: `${s.reason} — ใช้ Form ${s.form} เริ่ม ฿${s.priceFrom.toLocaleString()} เสร็จ ${s.turnaround} ยื่นจากไทยได้ทั้งหมด`,
      }}
      heroBullets={[
        `Form ${s.form} — ${s.who}`,
        `เริ่ม ฿${s.priceFrom.toLocaleString()}`,
        `เสร็จ ${s.turnaround}`,
        "แปล + Notary + Apostille ครบในแพ็กเกจ",
      ]}
      whoFor={{ th: [s.who], foreign: [s.en] }}
      steps={[
        { name: "ตรวจสิทธิ์", text: s.who },
        { name: "เตรียมเอกสาร", text: s.docs.join(" · ") },
        { name: "แปล+Notary+Apostille", text: "รับรองเอกสารต่างชาติให้อินเดียยอมรับ" },
        { name: "ยื่น NSDL", text: "ยื่นออนไลน์ + ส่งเอกสารด่วน" },
        { name: "รับ PAN", text: `e-PAN + บัตรจริง ${s.turnaround}` },
      ]}
      pricing={[
        { label: `บริการครบวงจร PAN — ${s.th}`, price: `${s.priceFrom.toLocaleString()}+`, note: s.turnaround },
        { label: "แปล + Notary + Apostille", price: "รวมในราคา", note: "ตามจำนวนเอกสาร" },
        { label: "Express NSDL Priority", price: "+2,000", note: "ลดเวลา 5–7 วัน" },
      ]}
      faqs={[
        { q: `${s.th} ทำไมต้องมี PAN?`, a: s.reason },
        { q: "เอกสารต้องใช้อะไรบ้าง?", a: s.docs.join(" · ") },
        { q: "Form อะไร?", a: `Form ${s.form}` },
        { q: "ราคาและเวลา?", a: `฿${s.priceFrom.toLocaleString()}+ / ${s.turnaround}` },
        { q: "ต้องบินไปอินเดียไหม?", a: "ไม่ต้องเดินทางไปอินเดียเลย — ยื่นออนไลน์ผ่าน NSDL (Protean eGov) แนบสแกนพาสปอร์ต รูปถ่าย และลายเซ็น ระบบข้าม Aadhaar biometric e-KYC สำหรับ NRI/foreigner โดยอัตโนมัติ NYC จัดการฟอร์ม 49AA เซ็นดิจิทัล ส่งบัตรพลาสติกกลับกรุงเทพ และออก e-PAN PDF ให้ภายใน 5–10 วันทำการหลังยื่น" },
      ]}
      relatedLinks={[
        { to: "/pan-card", label: "← PAN Card hub" },
        ...PAN_SCENARIOS.filter((x) => x.slug !== s.slug).slice(0, 4).map((x) => ({
          to: `/pan-card/${x.slug}` as const, label: `PAN — ${x.th}`,
        })) as any,
        { to: "/tax/dta/india", label: "DTAA อินเดีย–ไทย" },
      ]}
    />
  );
}
